import type { TopicSection } from '@researcher/shared';
import CitationPopover from './CitationPopover';

interface TopicSectionRendererProps {
  section: TopicSection;
}

/**
 * Renders a topic section with markdown-like formatting and interactive citation popovers.
 * Replaces [1], [2], etc. markers with clickable CitationPopover components.
 */
export default function TopicSectionRenderer({ section }: TopicSectionRendererProps) {
  // Render markdown-like content with simple formatting
  const renderFormattedContent = () => {
    const content = section.content;
    const lines = content.split('\n');

    return lines.map((line, lineIdx) => {
      const trimmed = line.trim();

      // Headers
      if (trimmed.startsWith('### ')) {
        return (
          <h4 key={lineIdx} className="text-sm font-semibold text-gray-800 dark:text-gray-100 mt-4 mb-2">
            {renderInline(trimmed.substring(4))}
          </h4>
        );
      }

      // Bullet points
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        return (
          <li key={lineIdx} className="text-sm text-gray-700 dark:text-gray-300 ml-4 mb-1">
            {renderInline(trimmed.substring(2))}
          </li>
        );
      }

      // Empty lines
      if (trimmed === '') {
        return <div key={lineIdx} className="h-2" />;
      }

      // Regular paragraphs
      return (
        <p key={lineIdx} className="text-sm text-gray-700 dark:text-gray-300 mb-2 leading-relaxed">
          {renderInline(trimmed)}
        </p>
      );
    });
  };

  const renderInline = (text: string) => {
    const parts: (string | JSX.Element)[] = [];
    // Handle **bold**, citations [N], and regular text
    const regex = /(\*\*(.+?)\*\*|\[(\d+)\])/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }

      if (match[2]) {
        // Bold
        parts.push(
          <strong key={`bold-${match.index}`} className="font-semibold text-gray-800 dark:text-gray-100">
            {match[2]}
          </strong>
        );
      } else if (match[3]) {
        // Citation
        const num = parseInt(match[3], 10);
        const citation = section.citations[num - 1];
        if (citation) {
          parts.push(
            <CitationPopover key={`cite-${match.index}`} citation={citation} number={num} />
          );
        } else {
          parts.push(match[0]);
        }
      }

      lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts;
  };

  return (
    <div id={`section-${section.type}`} className="scroll-mt-24">
      <h3 className="text-base font-bold text-gray-800 dark:text-gray-100 mb-3 pb-2 border-b border-gray-100 dark:border-gray-800">
        {section.title}
      </h3>
      <div className="space-y-0">
        {renderFormattedContent()}
      </div>
    </div>
  );
}
